import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area } from 'recharts';
import api from '../lib/api';

const COLORS = ['#059669', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const PurchaseEntryDashboard = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/purchase-entries/dashboard/stats')
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" /></div>;
  if (!data) return <div className="text-center py-12 text-gray-500">No data available</div>;

  const { summary, po_status, quality, vendor_summary, daily_trend, monthly_trend, recent_entries } = data;

  const poStatusData = [
    { name: 'Completed', value: po_status.completed, color: '#059669' },
    { name: 'Partial', value: po_status.partial, color: '#f59e0b' },
    { name: 'Pending', value: po_status.pending, color: '#3b82f6' },
  ].filter(d => d.value > 0);

  const qualityData = [
    { name: 'OK', value: quality.ok, color: '#059669' },
    { name: 'Defective', value: quality.defective, color: '#ef4444' },
    { name: 'Shortfall', value: quality.shortfall, color: '#f59e0b' },
  ].filter(d => d.value > 0);

  const formatCurrency = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="pe-dashboard-container">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Purchase Entry Dashboard</h1>
        <p className="text-gray-600 mt-1">Receipt tracking, vendor performance, and stock trends</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="pe-dashboard-summary">
        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Total GRNs</p>
            <p className="text-3xl font-bold text-emerald-800 mt-1" data-testid="pe-total-entries">{summary.total_entries}</p>
            <p className="text-xs text-emerald-600 mt-1">{summary.total_qty.toLocaleString('en-IN')} pcs received</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Total Cost</p>
            <p className="text-3xl font-bold text-blue-800 mt-1" data-testid="pe-total-cost">{formatCurrency(summary.total_cost)}</p>
            <p className="text-xs text-blue-600 mt-1">across all receipts</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-amber-600 uppercase tracking-wide">PO Receipt Rate</p>
            <p className="text-3xl font-bold text-amber-800 mt-1" data-testid="pe-receipt-rate">
              {po_status.total > 0 ? Math.round((po_status.completed / po_status.total) * 100) : 0}%
            </p>
            <p className="text-xs text-amber-600 mt-1">{po_status.completed}/{po_status.total} POs completed</p>
          </CardContent>
        </Card>
        <Card className={`bg-gradient-to-br ${summary.overdue_pos > 0 ? 'from-red-50 to-rose-50 border-red-200' : 'from-green-50 to-emerald-50 border-green-200'}`}>
          <CardContent className="p-4">
            <p className={`text-xs font-medium uppercase tracking-wide ${summary.overdue_pos > 0 ? 'text-red-600' : 'text-green-600'}`}>Overdue POs</p>
            <p className={`text-3xl font-bold mt-1 ${summary.overdue_pos > 0 ? 'text-red-800' : 'text-green-800'}`} data-testid="pe-overdue">{summary.overdue_pos}</p>
            <p className={`text-xs mt-1 ${summary.overdue_pos > 0 ? 'text-red-600' : 'text-green-600'}`}>{summary.overdue_pos > 0 ? 'needs attention' : 'all on track'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1: PO Status + Quality */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* PO Receipt Status Pie */}
        <Card data-testid="pe-po-status-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">PO Receipt Status</CardTitle>
            <CardDescription>Pending vs partial vs completed purchase orders</CardDescription>
          </CardHeader>
          <CardContent>
            {poStatusData.length === 0 ? (
              <p className="text-center text-gray-400 py-8">No PO data</p>
            ) : (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={180}>
                  <PieChart>
                    <Pie data={poStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3}>
                      {poStatusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `${v} POs`} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-3 flex-1">
                  {poStatusData.map(d => (
                    <div key={d.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                        <span className="text-sm text-gray-600">{d.name}</span>
                      </div>
                      <span className="text-sm font-bold">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quality Breakdown Pie */}
        <Card data-testid="pe-quality-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Quality Breakdown</CardTitle>
            <CardDescription>OK vs defective vs shortfall received items</CardDescription>
          </CardHeader>
          <CardContent>
            {qualityData.length === 0 ? (
              <p className="text-center text-gray-400 py-8">No quality data</p>
            ) : (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={180}>
                  <PieChart>
                    <Pie data={qualityData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3}>
                      {qualityData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `${v} pcs`} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-3 flex-1">
                  {qualityData.map(d => (
                    <div key={d.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                        <span className="text-sm text-gray-600">{d.name}</span>
                      </div>
                      <span className="text-sm font-bold">{d.value.toLocaleString('en-IN')} pcs</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stock Received Trend (Area Chart) */}
      <Card data-testid="pe-trend-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Stock Received Trend</CardTitle>
          <CardDescription>Daily quantity and cost of received goods (last 30 days)</CardDescription>
        </CardHeader>
        <CardContent>
          {daily_trend.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No trend data yet — create purchase entries to see trends</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={daily_trend}>
                <defs>
                  <linearGradient id="qtyGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#059669" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={v => v.slice(5)} />
                <YAxis yAxisId="qty" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="cost" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v, name) => name === 'Qty' ? `${v} pcs` : formatCurrency(v)} labelFormatter={l => `Date: ${l}`} />
                <Legend />
                <Area yAxisId="qty" type="monotone" dataKey="qty" name="Qty" stroke="#059669" fillOpacity={1} fill="url(#qtyGradient)" strokeWidth={2} />
                <Area yAxisId="cost" type="monotone" dataKey="cost" name="Cost" stroke="#3b82f6" fillOpacity={1} fill="url(#costGradient)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Vendor-wise Summary */}
      <Card data-testid="pe-vendor-summary">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Vendor-wise Receipt Summary</CardTitle>
          <CardDescription>Total receipts by vendor, sorted by quantity</CardDescription>
        </CardHeader>
        <CardContent>
          {vendor_summary.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No vendor data</p>
          ) : (
            <div className="space-y-4">
              {/* Bar chart */}
              <ResponsiveContainer width="100%" height={Math.max(200, vendor_summary.length * 40)}>
                <BarChart data={vendor_summary} layout="vertical" margin={{ left: 120 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="vendor_name" tick={{ fontSize: 11 }} width={110} />
                  <Tooltip formatter={(v, name) => name === 'Qty' ? `${v} pcs` : formatCurrency(v)} />
                  <Legend />
                  <Bar dataKey="total_qty" name="Qty" fill="#059669" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold text-gray-600">Vendor</th>
                      <th className="text-center px-4 py-2 font-semibold text-gray-600">GRNs</th>
                      <th className="text-right px-4 py-2 font-semibold text-gray-600">Total Qty</th>
                      <th className="text-right px-4 py-2 font-semibold text-gray-600">Total Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendor_summary.map((v, i) => (
                      <tr key={v.vendor_id} className="border-b hover:bg-gray-50/80">
                        <td className="px-4 py-2 font-medium">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                            {v.vendor_name}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-center">{v.entries}</td>
                        <td className="px-4 py-2 text-right font-medium">{v.total_qty.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-2 text-right font-medium">{formatCurrency(v.total_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Monthly Trend */}
      {monthly_trend.length > 0 && (
        <Card data-testid="pe-monthly-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Monthly Receipts</CardTitle>
            <CardDescription>Quantity and cost trends month-over-month</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="qty" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="cost" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v, name) => name === 'Quantity' ? `${v} pcs` : formatCurrency(v)} />
                <Legend />
                <Bar yAxisId="qty" dataKey="qty" name="Quantity" fill="#059669" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="cost" dataKey="cost" name="Cost" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Recent Entries */}
      {recent_entries.length > 0 && (
        <Card data-testid="pe-recent-entries">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Recent Receipts</CardTitle>
            <CardDescription>Last 10 purchase entries</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600">GRN</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600">Vendor</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600">Date</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600">Invoice</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600">Items</th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-600">Qty</th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-600">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_entries.map(e => (
                    <tr key={e.id} className="border-b hover:bg-gray-50/80">
                      <td className="px-3 py-2 font-semibold text-emerald-700">{e.grn_number}</td>
                      <td className="px-3 py-2">{e.vendor_name}</td>
                      <td className="px-3 py-2 text-xs">{new Date(e.entry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                      <td className="px-3 py-2 text-xs">{e.invoice_number || e.challan_number || '-'}</td>
                      <td className="px-3 py-2">
                        {(e.items || []).map((it, i) => <Badge key={i} variant="outline" className="text-[10px] mr-1">{it.design_code}</Badge>)}
                      </td>
                      <td className="px-3 py-2 text-right font-medium">{e.total_qty}</td>
                      <td className="px-3 py-2 text-right font-medium">{formatCurrency(e.total_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PurchaseEntryDashboard;
